import React, { useEffect, useRef, useState } from 'react';
import { FormControl, FormHelperText, Icon, InputLabel, MenuItem, Select, TextField } from '@mui/material';

import DialogBase from './DialogBase';
import { Task } from '../../../models/portfolio/task';
import { contentService } from '../../../services/content.service';
import { DatePicker, DateTimePicker } from '@mui/x-date-pickers';

import dayjs from "dayjs";
import "dayjs/locale/nl"; // Import Dutch locale


interface DialogTaskAddProps {
  open: boolean;
  selectedTask?: Task;
  portfolioId?: string;
  organizations?: any[];
  onClose: () => void;
  onSubmit: (task: any) => void;
}


const MIN_DATE = dayjs(); // dayjs('1970-01-01');
// const MAX_DATE = dayjs('2500-12-31');

const MAX_LENGTH = 2000;
const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 400,
    },
  },
};

const DialogTaskAdd: React.FC<DialogTaskAddProps> = ({
  open,
  selectedTask,
  portfolioId,
  organizations,
  onClose,
  onSubmit
}) => {
  const [task, setTask] = useState<Task>({} as Task);
  const [taskStatusOptions, setStatusses] = useState<any[]>([]);
  const [charCount, setCharCount] = useState(task?.description?.length || 0);

  const [validationErrors, setValidationErrors] = useState<{ 
    status?: string;
    title?: string;
    description?: string;
    assignedOrganization?: string;
    dueDate?: string;
  }>({});

  const hasStatussesData = useRef(false);
  const isEditMode = Boolean(selectedTask?.id);
  
  useEffect(() => {
    // Ensure the fetch happens only once
    if (!hasStatussesData.current) {
      getTaskStatusses();
      hasStatussesData.current = true; // Mark as fetched
    }
  }, []);

  useEffect(() => {
    if (open) {
      if (selectedTask) {
        setTask(selectedTask);
        setCharCount(selectedTask.description?.length || 0);
      } 
      else {
        setTask({ 
          portfolioId: portfolioId
        } as Task);
        setCharCount(0);
      }
      setValidationErrors({});
    }
  }, [open, selectedTask, portfolioId]);

  const getTaskStatusses = async () => {
    const statusses = await contentService.getTaskStatusses();
    setStatusses(statusses);
  };

  const onChange = (key: keyof Task, value: any) => {
    setTask(prev => ({ ...prev, [key]: value }));
  };

  const handleSubmit = () => {
    const errors: typeof validationErrors = {};

    if (!task.status) errors.status = 'Status is verplicht';
    if (!task.assignedOrganization) errors.assignedOrganization = 'Organisatie is verplicht';
    if (!task.title || task.title.trim() === '') errors.title = 'Titel is verplicht';
    if (task.description && task.description.length > MAX_LENGTH) errors.description = 'Omschrijving mag maximaal 2000 tekens bevatten';
    if (!task.dueDate) errors.dueDate = 'Einddatum is verplicht';

    // if (!task.description || task.description.trim() === '') {
    //   errors.description = 'Omschrijving is verplicht';
    // }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    onSubmit(task);
    onClose();
  };

  return (
    <DialogBase
      open={open}
      title={isEditMode ? 'Actie wijzigen' : 'Actie toevoegen'}
      contentText=""
      onClose={onClose}
      onConfirm={handleSubmit}
      confirmButtonText="Opslaan"
      confirmButtonIcon={<Icon>save</Icon>}
      width="1000px"
      minWidth="800px"
    >
      <FormControl 
        fullWidth 
        required 
        sx={{ my: 1 }}
        >
        <TextField
          required
          label="Titel"
          variant="filled"
          value={task.title ?? ''}
          onChange={(e) => onChange('title', e.target.value)}
          error={!!validationErrors.title}
          helperText={validationErrors.title}
          // InputLabelProps={{ shrink: !!task.title }}
        />
      </FormControl>

      <FormControl 
        fullWidth 
        required 
        className="select"
        sx={{ my: 1 }}
        >
        <InputLabel id="select-status-label">Kies een status</InputLabel>
        <Select
          labelId="select-status-label"
          id="status"
          variant="filled"
          disabled={!(taskStatusOptions.length > 0)}
          onChange={(e) => onChange('status', e.target.value)}
          value={task.status || ''}
          error={!!validationErrors.status}
          MenuProps={MenuProps}
        >
          <MenuItem value=""><em>&nbsp;</em></MenuItem>
          {taskStatusOptions && taskStatusOptions.map((option) => (
            <MenuItem key={option.value} value={String(option.value)}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
        {validationErrors.status && (
          <FormHelperText error>{validationErrors.status}</FormHelperText>
        )}
      </FormControl>

      <FormControl 
        fullWidth 
        required 
        className="select"
        sx={{ my: 1 }}
        >
        <InputLabel id="select-organization-label">Kies een organisatie</InputLabel>
        <Select
          labelId="select-organization-label"
          id="assigned-organization"
          variant="filled"
          disabled={!(organizations && organizations.length > 0)}
          onChange={(e) => onChange('assignedOrganization', e.target.value)}
          value={task.assignedOrganization || ''}
          error={!!validationErrors.assignedOrganization}
          MenuProps={MenuProps}
        >
          <MenuItem value=""><em>&nbsp;</em></MenuItem>
          {organizations && organizations.filter((org) => org.active).map((org) => (
            <MenuItem key={org.id} value={org.id}>
              {org.name}
            </MenuItem>
          ))}
        </Select>
        {validationErrors.assignedOrganization && (
          <FormHelperText error>{validationErrors.assignedOrganization}</FormHelperText>
        )}
      </FormControl>

      <FormControl 
        fullWidth 
        required sx={{ my: 1 }}>
        <DatePicker
          label="Deadline"
          value={task.dueDate ? dayjs(task.dueDate) : null}
          onChange={(newValue) => {
            // If empty or invalid input, clear the date safely
            if (!newValue || !newValue.isValid()) {
              onChange('dueDate', '');
              return;
            }
          
            const iso = newValue ? newValue.startOf('day').toISOString() : '';
            onChange('dueDate', iso);
          }}
          format="DD/MM/YYYY"
          minDate={MIN_DATE}
          slots={{ textField: TextField }}
          slotProps={{
            textField: {
              fullWidth: true,
              required: true,
              variant: 'filled',
              error: !!validationErrors.dueDate,
              helperText: validationErrors.dueDate,
            },
          }}
        />

        {/* <DateTimePicker
          label="Deadline"
          value={task.dueDate ? dayjs(task.dueDate) : null}
          onChange={(newValue) => {
            onChange('dueDate', newValue?.toISOString() ?? '');
          }}
          ampm={false}
          slots={{ textField: TextField }}
          // desktopModeMediaQuery="@media (min-width:9999px)" // force mobile mode
          slotProps={{
            textField: {
              fullWidth: true,
              variant: 'filled',
              // size: 'small',
              error: !!validationErrors.dueDate,
              helperText: validationErrors.dueDate,
            }
          }}
        /> */}
      </FormControl>

      <FormControl fullWidth required sx={{ my: 1 }}>
        <TextField
          fullWidth
          label="Omschrijving"
          variant="filled"
          multiline
          rows={8}
          onChange={(e) => {
            onChange('description', e.target.value);
            setCharCount(e.target.value.length);
          }}
          value={task.description || ''}
          error={!!validationErrors.description}
          helperText={validationErrors.description}
          inputProps={{ maxLength: MAX_LENGTH }}
        />
        <FormHelperText
          sx={{
            textAlign: 'right',
            fontSize: '0.8rem',
            color: charCount >= MAX_LENGTH ? 'error.main' : 'text.secondary',
            marginRight: 1,
          }}
        >
          {charCount} / {MAX_LENGTH}
        </FormHelperText>
      </FormControl>
    </DialogBase>
  );
};

export default DialogTaskAdd;
